import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  Search, Send, CheckSquare, Square, X, Loader2,
  CheckCircle, AlertCircle, RefreshCw, Upload,
  FileSpreadsheet, ChevronRight, ChevronLeft,
} from "lucide-react";
import { useBoletos } from "../../hooks/useBoletos";
import { useSelectedEvent } from "../../contexts/SelectedEventContext";

// ─── Helper: leer cabeceras del Excel en el cliente ──────────────────────────
async function leerCabecerasExcel(file) {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);
  const worksheet = workbook.worksheets[0];
  if (!worksheet) throw new Error("El archivo no contiene hojas");
  const headers = [];
  worksheet.getRow(1).eachCell({ includeEmpty: false }, (cell) => {
    const value = cell.value ? String(cell.value).trim() : "";
    if (value) headers.push(value);
  });
  if (headers.length === 0) throw new Error("La primera fila no contiene encabezados");
  return headers;
}

// ─── Subcomponente selector de columna ───────────────────────────────────────
const SelectCol = ({ label, value, onChange, columnas, required = false }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-casal/50"
    >
      <option value="">-- Seleccionar columna --</option>
      {columnas.map((c) => (
        <option key={c} value={c}>{c}</option>
      ))}
    </select>
  </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────
export default function CompartirExcelModal({ isOpen, onClose }) {
  const { eventoActual } = useSelectedEvent();
  const eventoId = eventoActual?.id;
  const { listarBoletosDisponibles, enviarBoletosSistema, enviarBoletosExcel } = useBoletos();

  // Modo: "sistema" | "excel"
  const [modo, setModo] = useState("sistema");

  // ── Estado modo SISTEMA ───────────────────────────────────────────────────
  const [boletos, setBoletos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [enviandoSistema, setEnviandoSistema] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [errorSistema, setErrorSistema] = useState(null);
  const [resultadoSistema, setResultadoSistema] = useState(null);

  // ── Estado modo EXCEL ─────────────────────────────────────────────────────
  const [paso, setPaso] = useState(0);
  const [archivoExcel, setArchivoExcel] = useState(null);
  const [columnas, setColumnas] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [parsando, setParsando] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [mapeo, setMapeo] = useState({
    columna_nombre: "", columna_correo: "", columna_boletos: "",
    columna_correo_secundario: "", columnas_qr: [],
  });
  const [enviandoExcel, setEnviandoExcel] = useState(false);
  const [resultadoExcel, setResultadoExcel] = useState(null);
  const inputFileRef = useRef(null);

  // ── Cargar lista al abrir (modo sistema) ──────────────────────────────────
  useEffect(() => {
    if (!isOpen || !eventoId) return;
    setBusqueda("");
    setSeleccionados(new Set());
    setErrorSistema(null);
    setResultadoSistema(null);
    const cargar = async () => {
      setCargando(true);
      try {
        const result = await listarBoletosDisponibles(eventoId);
        if (result.success) setBoletos(result.data.boletos || []);
        else setErrorSistema(result.error || "Error al cargar graduados");
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, [isOpen, eventoId, listarBoletosDisponibles]);

  const cerrar = () => {
    setResultadoSistema(null);
    setResultadoExcel(null);
    setBoletos([]);
    setPaso(0);
    setArchivoExcel(null);
    setColumnas([]);
    setMapeo({ columna_nombre: "", columna_correo: "", columna_boletos: "", columna_correo_secundario: "", columnas_qr: [] });
    setModo("sistema");
    onClose();
  };

  // ── Lógica modo SISTEMA ───────────────────────────────────────────────────
  const boletosFiltrados = boletos.filter((b) =>
    b.nombre.toLowerCase().includes(busqueda.toLowerCase())
  );
  const todosSeleccionados =
    boletosFiltrados.length > 0 &&
    boletosFiltrados.every((b) => seleccionados.has(b.invitadoId));

  const toggleSeleccion = useCallback((invitadoId) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(invitadoId)) next.delete(invitadoId);
      else next.add(invitadoId);
      return next;
    });
  }, []);

  const toggleTodos = useCallback(() => {
    if (todosSeleccionados) {
      setSeleccionados((prev) => {
        const next = new Set(prev);
        boletosFiltrados.forEach((b) => next.delete(b.invitadoId));
        return next;
      });
    } else {
      setSeleccionados((prev) => {
        const next = new Set(prev);
        boletosFiltrados.forEach((b) => next.add(b.invitadoId));
        return next;
      });
    }
  }, [todosSeleccionados, boletosFiltrados]);

  const handleEnviarSistema = async () => {
    if (seleccionados.size === 0 || enviandoSistema) return;
    setEnviandoSistema(true);
    setErrorSistema(null);
    try {
      const result = await enviarBoletosSistema(eventoId, [...seleccionados]);
      if (result.success) setResultadoSistema(result.data);
      else setErrorSistema(result.error || "Error al enviar boletos");
    } finally {
      setEnviandoSistema(false);
    }
  };

  // ── Lógica modo EXCEL ─────────────────────────────────────────────────────
  const procesarArchivo = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      setParseError("Solo se aceptan archivos .xlsx o .xls");
      return;
    }
    setParsando(true);
    setParseError(null);
    setArchivoExcel(file);
    try {
      const cols = await leerCabecerasExcel(file);
      setColumnas(cols);
      const autoMapeo = { ...mapeo };
      cols.forEach((c) => {
        const lower = c.toLowerCase();
        if (!autoMapeo.columna_nombre && (lower.includes("nombre") || lower === "name"))
          autoMapeo.columna_nombre = c;
        if (!autoMapeo.columna_correo && (lower.includes("correo") || lower.includes("email") || lower.includes("mail")))
          autoMapeo.columna_correo = c;
        if (!autoMapeo.columna_boletos && (lower.includes("boleto") || lower.includes("ticket") || lower.includes("cantidad")))
          autoMapeo.columna_boletos = c;
      });
      setMapeo(autoMapeo);
      setPaso(1);
    } catch (err) {
      setParseError(err.message || "Error al leer el archivo");
      setArchivoExcel(null);
    } finally {
      setParsando(false);
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    procesarArchivo(e.dataTransfer.files?.[0]);
  }, []);

  const mapeoValido = mapeo.columna_nombre && mapeo.columna_correo && mapeo.columna_boletos;

  const handleEnviarExcel = async () => {
    setEnviandoExcel(true);
    try {
      const result = await enviarBoletosExcel(archivoExcel, {
        columna_nombre: mapeo.columna_nombre,
        columna_correo: mapeo.columna_correo,
        columna_boletos: mapeo.columna_boletos,
        columna_correo_secundario: mapeo.columna_correo_secundario || undefined,
        columnas_qr: mapeo.columnas_qr,
      });
      setResultadoExcel(result);
      setPaso(3);
    } catch {
      setResultadoExcel({ success: false, error: "Error inesperado al enviar" });
      setPaso(3);
    } finally {
      setEnviandoExcel(false);
    }
  };

  // ── Pantalla de resultado SISTEMA ─────────────────────────────────────────
  if (resultadoSistema) {
    const { enviados = 0, fallidos = 0, detalle_fallidos = [] } = resultadoSistema;
    return (
      <Dialog open={isOpen} onClose={cerrar} className="relative z-50">
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md bg-white dark:bg-[#1e1e1e] rounded-2xl shadow-2xl p-6 space-y-4">
            <DialogTitle className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Resultado del envio
            </DialogTitle>
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <CheckCircle className="text-green-500 shrink-0" size={20} />
              <span className="text-sm text-green-700 font-medium">
                {enviados} boleto{enviados !== 1 ? "s" : ""} enviado{enviados !== 1 ? "s" : ""} correctamente
              </span>
            </div>
            {fallidos > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-amber-600 text-sm font-medium">
                  <AlertCircle size={16} />{fallidos} con errores:
                </div>
                <ul className="text-xs text-gray-500 space-y-1 max-h-32 overflow-y-auto pl-2">
                  {detalle_fallidos.map((f, i) => (
                    <li key={i} className="truncate">
                      <span className="font-medium text-gray-700">{f.nombre || f.id}</span>{" - "}{f.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button onClick={cerrar} className="px-5 py-2 bg-casal text-white rounded-lg text-sm font-medium hover:bg-casal/90 transition">
                Cerrar
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    );
  }

  // ── Render EXCEL paso 3 (resultado) ──────────────────────────────────────
  const renderExcelPaso3 = () => {
    if (!resultadoExcel) return null;
    const { data } = resultadoExcel;
    const enviados = data?.enviados ?? 0;
    const fallidos = data?.fallidos ?? 0;
    const detalleFallidos = data?.detalle_fallidos ?? [];
    return (
      <div className="space-y-4 py-2">
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <CheckCircle className="text-green-500 shrink-0" size={20} />
          <span className="text-sm text-green-700 font-medium">
            {enviados} boleto{enviados !== 1 ? "s" : ""} enviado{enviados !== 1 ? "s" : ""} correctamente
          </span>
        </div>
        {fallidos > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-amber-600 text-sm font-medium">
              <AlertCircle size={16} />{fallidos} con errores
            </div>
            <ul className="text-xs text-gray-500 space-y-1 max-h-28 overflow-y-auto pl-2">
              {detalleFallidos.map((f, i) => (
                <li key={i}><span className="font-medium text-gray-700">{f.fila || f.nombre}</span>{" - "}{f.error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // ── Vista principal ───────────────────────────────────────────────────────
  return (
    <Dialog open={isOpen} onClose={cerrar} className="relative z-50">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg bg-white dark:bg-[#1e1e1e] rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">

          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
            <DialogTitle className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Enviar boletos por correo
            </DialogTitle>
            <button onClick={cerrar} className="text-gray-400 hover:text-gray-600 transition">
              <X size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100 dark:border-gray-700 px-6">
            <button
              onClick={() => setModo("sistema")}
              className={`py-3 px-1 mr-6 text-sm font-medium border-b-2 transition ${
                modo === "sistema"
                  ? "border-casal text-casal"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              Datos del sistema
            </button>
            <button
              onClick={() => setModo("excel")}
              className={`py-3 px-1 text-sm font-medium border-b-2 transition ${
                modo === "excel"
                  ? "border-casal text-casal"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              Subir Excel
            </button>
          </div>

          {/* ── MODO SISTEMA ────────────────────────────────────────────── */}
          {modo === "sistema" && (
            <>
              {/* Buscador */}
              <div className="px-6 pt-4 pb-2">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por nombre..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-casal/40"
                  />
                </div>
              </div>

              {/* Seleccionar todos */}
              {!cargando && boletosFiltrados.length > 0 && (
                <div className="px-6 py-2 flex items-center justify-between text-sm">
                  <button
                    onClick={toggleTodos}
                    className="flex items-center gap-2 text-casal hover:text-casal/80 font-medium transition"
                  >
                    {todosSeleccionados ? <CheckSquare size={16} /> : <Square size={16} />}
                    {todosSeleccionados ? "Desmarcar todos" : "Seleccionar todos"}
                    {busqueda && ` (${boletosFiltrados.length} visibles)`}
                  </button>
                  <span className="text-gray-400">
                    {seleccionados.size} seleccionado{seleccionados.size !== 1 ? "s" : ""}
                  </span>
                </div>
              )}

              {/* Lista */}
              <div className="flex-1 overflow-y-auto px-6 py-2 min-h-0">
                {cargando ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 size={24} className="animate-spin text-casal" />
                    <span className="ml-3 text-sm text-gray-500">Cargando graduados...</span>
                  </div>
                ) : errorSistema ? (
                  <div className="text-center py-10 text-red-500 text-sm">{errorSistema}</div>
                ) : boletosFiltrados.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 text-sm">
                    {busqueda ? "Sin resultados" : "No hay graduados registrados para este evento"}
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {boletosFiltrados.map((boleto) => {
                      const marcado = seleccionados.has(boleto.invitadoId);
                      return (
                        <li key={boleto.invitadoId}>
                          <button
                            onClick={() => toggleSeleccion(boleto.invitadoId)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition ${
                              marcado
                                ? "bg-casal/10 text-casal"
                                : "hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                            }`}
                          >
                            {marcado
                              ? <CheckSquare size={16} className="shrink-0" />
                              : <Square size={16} className="shrink-0 text-gray-300" />}
                            <span className="text-sm font-medium truncate flex-1">
                              {boleto.nombre}
                            </span>
                            {boleto.generado
                              ? <span className="text-xs text-green-500 shrink-0" title="Boleto ya generado">&#10003;</span>
                              : <RefreshCw size={11} className="text-gray-300 shrink-0" title="Se generara al enviar" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Footer sistema */}
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
                <p className={`text-xs flex-1 truncate ${errorSistema ? "text-red-500" : "text-gray-400"}`}>
                  {errorSistema
                    ? errorSistema
                    : seleccionados.size === 0
                      ? "Selecciona graduados para enviar sus boletos"
                      : `Se enviara a ${seleccionados.size} graduado${seleccionados.size !== 1 ? "s" : ""}`}
                </p>
                <button
                  onClick={handleEnviarSistema}
                  disabled={seleccionados.size === 0 || enviandoSistema}
                  className="flex items-center gap-2 px-5 py-2 bg-casal text-white rounded-lg text-sm font-medium hover:bg-casal/90 transition disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  {enviandoSistema
                    ? <><Loader2 size={16} className="animate-spin" />Enviando...</>
                    : <><Send size={16} />Enviar {seleccionados.size > 0 ? `(${seleccionados.size})` : ""}</>}
                </button>
              </div>
            </>
          )}

          {/* ── MODO EXCEL ───────────────────────────────────────────────── */}
          {modo === "excel" && (
            <div className="flex-1 overflow-y-auto flex flex-col">
              {/* Pasos indicador */}
              {paso < 3 && (
                <div className="px-6 pt-4 pb-2 flex items-center gap-2 text-xs text-gray-400">
                  {["Subir Excel", "Mapear columnas", "Confirmar"].map((label, i) => (
                    <React.Fragment key={i}>
                      <span className={i === paso ? "font-semibold text-casal" : ""}>{i + 1}. {label}</span>
                      {i < 2 && <ChevronRight size={12} />}
                    </React.Fragment>
                  ))}
                </div>
              )}

              <div className="px-6 py-4 flex-1">
                {/* Paso 0: subir archivo */}
                {paso === 0 && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                      Sube un Excel con los datos de los invitados. Se generara un boleto con QR y se enviara por correo a cada persona.
                    </p>
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={onDrop}
                      onClick={() => inputFileRef.current?.click()}
                      className={`border-2 border-dashed rounded-2xl p-10 flex flex-col items-center cursor-pointer transition-colors ${
                        dragging ? "border-casal bg-casal/5" : "border-gray-300 hover:border-casal/60 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      }`}
                    >
                      <FileSpreadsheet className={`w-12 h-12 mb-3 ${dragging ? "text-casal" : "text-gray-400"}`} />
                      {parsando ? (
                        <div className="flex items-center gap-2 text-casal">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm font-medium">Leyendo archivo...</span>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Arrastra tu archivo aqui</p>
                          <p className="text-xs text-gray-400 mt-1">o haz clic para buscarlo</p>
                          <p className="text-xs text-gray-400 mt-2">Formatos: .xlsx, .xls</p>
                        </>
                      )}
                    </div>
                    <input ref={inputFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => procesarArchivo(e.target.files?.[0])} />
                    {parseError && (
                      <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm">
                        <AlertCircle className="w-4 h-4 shrink-0" />{parseError}
                      </div>
                    )}
                  </div>
                )}

                {/* Paso 1: mapeo de columnas */}
                {paso === 1 && (
                  <div className="space-y-4">
                    <div className="bg-casal/5 border border-casal/20 rounded-xl px-4 py-2 flex items-center gap-2 text-sm text-casal">
                      <FileSpreadsheet className="w-4 h-4 shrink-0" />
                      <span className="font-medium truncate">{archivoExcel?.name}</span>
                      <span className="text-casal/60 shrink-0">— {columnas.length} columnas</span>
                    </div>
                    <SelectCol label="Columna Nombre" value={mapeo.columna_nombre} onChange={(v) => setMapeo((p) => ({ ...p, columna_nombre: v }))} columnas={columnas} required />
                    <SelectCol label="Columna Correo Principal" value={mapeo.columna_correo} onChange={(v) => setMapeo((p) => ({ ...p, columna_correo: v }))} columnas={columnas} required />
                    <SelectCol label="Columna No. de Boletos" value={mapeo.columna_boletos} onChange={(v) => setMapeo((p) => ({ ...p, columna_boletos: v }))} columnas={columnas} required />
                    <SelectCol label="Columna Correo Secundario (opcional)" value={mapeo.columna_correo_secundario} onChange={(v) => setMapeo((p) => ({ ...p, columna_correo_secundario: v }))} columnas={["", ...columnas]} />
                  </div>
                )}

                {/* Paso 2: confirmar */}
                {paso === 2 && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">Se enviaran boletos usando estas columnas:</p>
                    <dl className="space-y-2 text-sm">
                      {[
                        ["Nombre", mapeo.columna_nombre],
                        ["Correo principal", mapeo.columna_correo],
                        ["No. boletos", mapeo.columna_boletos],
                        mapeo.columna_correo_secundario && ["Correo secundario", mapeo.columna_correo_secundario],
                      ].filter(Boolean).map(([label, val]) => (
                        <div key={label} className="flex gap-3">
                          <dt className="text-gray-400 w-36 shrink-0">{label}</dt>
                          <dd className="font-medium text-gray-700 dark:text-gray-200 truncate">{val}</dd>
                        </div>
                      ))}
                    </dl>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
                      Este proceso puede tardar varios minutos dependiendo de la cantidad de filas.
                    </div>
                  </div>
                )}

                {/* Paso 3: resultado */}
                {paso === 3 && renderExcelPaso3()}
              </div>

              {/* Footer Excel */}
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-between gap-3">
                {paso > 0 && paso < 3 ? (
                  <button
                    onClick={() => setPaso((p) => p - 1)}
                    disabled={enviandoExcel}
                    className="flex items-center gap-1 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg transition disabled:opacity-40"
                  >
                    <ChevronLeft size={16} />Atrás
                  </button>
                ) : <div />}

                {paso === 0 && <div />}

                {paso === 1 && (
                  <button
                    onClick={() => setPaso(2)}
                    disabled={!mapeoValido}
                    className="flex items-center gap-1 px-5 py-2 bg-casal text-white rounded-lg text-sm font-medium hover:bg-casal/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Continuar<ChevronRight size={16} />
                  </button>
                )}

                {paso === 2 && (
                  <button
                    onClick={handleEnviarExcel}
                    disabled={enviandoExcel}
                    className="flex items-center gap-2 px-5 py-2 bg-casal text-white rounded-lg text-sm font-medium hover:bg-casal/90 transition disabled:opacity-40"
                  >
                    {enviandoExcel
                      ? <><Loader2 size={16} className="animate-spin" />Enviando...</>
                      : <><Send size={16} />Enviar boletos</>}
                  </button>
                )}

                {paso === 3 && (
                  <button onClick={cerrar} className="px-5 py-2 bg-casal text-white rounded-lg text-sm font-medium hover:bg-casal/90 transition">
                    Cerrar
                  </button>
                )}
              </div>
            </div>
          )}

        </DialogPanel>
      </div>
    </Dialog>
  );
}
